import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import DynamicProfessionalSearch from '@/components/DynamicProfessionalSearch';
import SearchHeader from '@/components/search/SearchHeader';
import SearchFilterSidebar from '@/components/search/SearchFilterSidebar';
import SearchMobileFilters from '@/components/search/SearchMobileFilters';
import SearchResults from '@/components/search/SearchResults';
import SearchLandingView from '@/components/search/SearchLandingView';
import useSearchProfessionals from '@/hooks/useSearchProfessionals';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDirection } from '@/providers/DirectionProvider';

const Search = () => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const isMobile = useIsMobile();
  const {
    professionals,
    loading,
    error,
    mobileFiltersOpen,
    setMobileFiltersOpen,
    selectedCategory,
    selectedLocation,
    displayProfessionName,
    availableSpecialties,
    sortOption,
    filters,
    setFilters,
    handleSearch,
    toggleCategory,
    handleFilterApply,
    resetFilters,
    handleSortChange,
    handlePhoneReveal,
    searchResultMessage,
    searchedProfessionId,
    hasSearched,
    resetSearch
  } = useSearchProfessionals();

  // Handle profession selection from landing view
  const handleProfessionSelect = (professionId: string, displayName: string) => {
    handleSearch(professionId, 'all', displayName);
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen" dir={direction}>
        <Header />
        <main className="flex-grow flex items-center justify-center pt-28">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('search.loadingProfessionals')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen" dir={direction}>
        <Header />
        <main className="flex-grow flex items-center justify-center pt-28">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('search.errorLoading')}</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
              onClick={() => window.location.reload()}
            >
              {t('common.tryAgain')}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Generate dynamic SEO based on search
  const getSeoTitle = () => {
    if (hasSearched && displayProfessionName) {
      return t('seo.professionSearchTitle', { profession: displayProfessionName });
    }
    return t('seo.searchTitle');
  };

  const getSeoDescription = () => {
    if (hasSearched && displayProfessionName) {
      return t('seo.professionSearchDescription', { profession: displayProfessionName });
    }
    return t('seo.searchDescription');
  };

  const getSeoKeywords = () => {
    if (hasSearched && displayProfessionName) {
      return t('seo.professionSearchKeywords', { profession: displayProfessionName });
    }
    return t('seo.searchKeywords');
  };

  return (
    <div className="flex flex-col min-h-screen" dir={direction}>
      <Helmet>
        <title>{getSeoTitle()}</title>
        <meta name="description" content={getSeoDescription()} />
        <meta name="keywords" content={getSeoKeywords()} />
        
        {/* Open Graph */}
        <meta property="og:title" content={getSeoTitle()} />
        <meta property="og:description" content={getSeoDescription()} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://ofair.co.il/search${searchedProfessionId ? `?profession=${searchedProfessionId}` : ''}`} />
        <meta property="og:locale" content="he_IL" />
        <meta property="og:site_name" content="oFair - עופר" />
        
        {/* Canonical */}
        <link rel="canonical" href={`https://ofair.co.il/search${searchedProfessionId ? `?profession=${searchedProfessionId}` : ''}`} />
        
        {/* Service Structured Data when searching for a profession */}
        {hasSearched && displayProfessionName && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "name": `שירותי ${displayProfessionName}`,
              "description": getSeoDescription(),
              "provider": {
                "@type": "Organization",
                "name": "oFair - עופר",
                "url": "https://ofair.co.il"
              },
              "areaServed": {
                "@type": "Country",
                "name": "Israel"
              },
              "serviceType": displayProfessionName
            })}
          </script>
        )}
      </Helmet>

      <Header />

      <main className="flex-grow pt-28 pb-16">
        <div className="container mx-auto px-4 sm:px-6 py-[21px]">
          {/* Page title */}
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-6 text-foreground">
            {t('search.pageTitle')}
          </h1>
          
          {/* Show header only when search has been performed */}
          {hasSearched && <SearchHeader displayProfessionName={displayProfessionName} />}

          {/* Search bar - always visible */}
          <div className={`mb-8 ${isMobile ? 'relative z-20 bg-gray-50 pt-4 pb-6 px-4 -mx-4 shadow-sm rounded-b-lg' : 'sticky top-28 z-30 bg-gray-50 pt-4 pb-6 px-4 -mx-4 shadow-sm rounded-b-lg'}`}>
            <DynamicProfessionalSearch onSearch={handleSearch} initialProfession="" initialLocation="" />
          </div>

          {/* Conditional rendering based on hasSearched */}
          {!hasSearched ? (
            // Landing view - before search
            <SearchLandingView onProfessionSelect={handleProfessionSelect} />
          ) : (
            // Results view - after search
            <>
              {/* Search results message */}
              {searchResultMessage && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-center">{searchResultMessage}</p>
                </div>
              )}

              <div className="flex flex-col md:flex-row gap-8">
                <SearchFilterSidebar
                  filters={filters}
                  setFilters={setFilters}
                  availableSpecialties={availableSpecialties}
                  handleFilterApply={handleFilterApply}
                  resetFilters={resetFilters}
                  toggleCategory={toggleCategory}
                />

                <div className="flex-grow">
                  <SearchMobileFilters
                    mobileFiltersOpen={mobileFiltersOpen}
                    setMobileFiltersOpen={setMobileFiltersOpen}
                    filters={filters}
                    setFilters={setFilters}
                    availableSpecialties={availableSpecialties}
                    handleFilterApply={handleFilterApply}
                    resetFilters={resetFilters}
                    toggleCategory={toggleCategory}
                  />

                  <SearchResults
                    professionals={professionals}
                    sortOption={sortOption}
                    handleSortChange={handleSortChange}
                    handlePhoneReveal={handlePhoneReveal}
                    resetFilters={resetFilters}
                    searchedProfessionId={searchedProfessionId || undefined}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Search;
